import { createContext, Dispatch, FC, ReactNode, SetStateAction, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IPermissionsState, MAIN_NODES, PERMISSIONS_ACTIONS } from "../config/permissions";
import { ILoginResponse } from "../interfaces/auth";

export interface IAuthContext {
    loginData: ILoginResponse | null;
    permissionsData: { [x: string]: string[] } | null;
    permissionsState: IPermissionsState | undefined;
    login: (loginData: ILoginResponse | null) => void;
    logout: () => void;
    setPermissions: (permissionsData: { [x: string]: string[] } | null) => void
    setLoginData: Dispatch<SetStateAction<ILoginResponse | null>>;
    setPermissionsData: Dispatch<SetStateAction<{ [x: string]: string[] } | null>>;
}

export const AUTH_KEY = "BEE2GETHER_AUTH";
export const PERMISSIONS_KEY = "BEE2GETHER_PERMISSIONS_USER";

export const AuthContext = createContext<IAuthContext | undefined>(undefined);

const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const navigate = useNavigate();

    const currentAuth = localStorage.getItem(AUTH_KEY);
    const currentPermissions = localStorage.getItem(PERMISSIONS_KEY);
    const [loginData, setLoginData] = useState<ILoginResponse | null>(
        currentAuth ? JSON.parse(currentAuth) : null
    );
    const [permissionsData, setPermissionsData] = useState<{ [x: string]: string[] } | null>(
        currentPermissions ? JSON.parse(currentPermissions) : null
    );

    const getPermissionsState = (permissions: { [x: string]: string[] } | null) => {
        const existBeehivesPermissions = !!permissions && !!permissions.bh && permissions.bh.length > 0
        const existAlertsPermissions = !!permissions && !!permissions.bhalerts && permissions.bhalerts.length > 0
        const existStatsPermissions = !!permissions && !!permissions.bhstats && permissions.bhstats.length > 0
        const existUsersPermissions = !!permissions && !!permissions.users && permissions.users.length > 0
        const existAreasPermissions = !!permissions && !!permissions.areas && permissions.areas.length > 0
        const existRolesPermissions = !!permissions && !!permissions.roles && permissions.roles.length > 0

        return {
            existBeehivesPermissions,
            existAlertsPermissions,
            existStatsPermissions,
            existUsersPermissions,
            existAreasPermissions,
            existRolesPermissions,
            // beehives
            existViewBeehivesPermission: existBeehivesPermissions && !!permissions.bh.find(perm => perm.includes(MAIN_NODES.BEEHIVES + "_" + PERMISSIONS_ACTIONS.VIEW)),
            existCreateBeehivesPermission: existBeehivesPermissions && !!permissions.bh.find(perm => perm.includes(MAIN_NODES.BEEHIVES + "_" + PERMISSIONS_ACTIONS.CREATE)),
            existEditBeehivesPermission: existBeehivesPermissions && !!permissions.bh.find(perm => perm.includes(MAIN_NODES.BEEHIVES + "_" + PERMISSIONS_ACTIONS.EDIT)),
            existDeleteBeehivesPermission: existBeehivesPermissions && !!permissions.bh.find(perm => perm.includes(MAIN_NODES.BEEHIVES + "_" + PERMISSIONS_ACTIONS.DELETE)),
            existDownloadBeehivesPermission: existBeehivesPermissions && !!permissions.bh.find(perm => perm.includes(MAIN_NODES.BEEHIVES + "_" + PERMISSIONS_ACTIONS.DOWNLOAD)),
            existDownloadAllBeehivesPermission: existBeehivesPermissions && !!permissions.bh.find(perm => perm.includes(MAIN_NODES.BEEHIVES + "_" + PERMISSIONS_ACTIONS.MASSIVE_DOWNLOAD)),
            // beehives alerts
            existViewAlertsPermission: existAlertsPermissions && !!permissions.bhalerts.find(perm => perm.includes(MAIN_NODES.BEEHIVES_ALERTS + "_" + PERMISSIONS_ACTIONS.VIEW)),
            existCreateAlertsPermission: existAlertsPermissions && !!permissions.bhalerts.find(perm => perm.includes(MAIN_NODES.BEEHIVES_ALERTS + "_" + PERMISSIONS_ACTIONS.CREATE)),
            existEditAlertsPermission: existAlertsPermissions && !!permissions.bhalerts.find(perm => perm.includes(MAIN_NODES.BEEHIVES_ALERTS + "_" + PERMISSIONS_ACTIONS.EDIT)),
            existDeleteAlertsPermission: existAlertsPermissions && !!permissions.bhalerts.find(perm => perm.includes(MAIN_NODES.BEEHIVES_ALERTS + "_" + PERMISSIONS_ACTIONS.DELETE)),
            // beehives stats
            existViewStatsPermission: existStatsPermissions && !!permissions.bhstats.find(perm => perm.includes(MAIN_NODES.BEEHIVES_STATS + "_" + PERMISSIONS_ACTIONS.VIEW)),
            existCreateStatsPermission: existStatsPermissions && !!permissions.bhstats.find(perm => perm.includes(MAIN_NODES.BEEHIVES_STATS + "_" + PERMISSIONS_ACTIONS.CREATE)),
            existEditStatsPermission: existStatsPermissions && !!permissions.bhstats.find(perm => perm.includes(MAIN_NODES.BEEHIVES_STATS + "_" + PERMISSIONS_ACTIONS.EDIT)),
            existDeleteStatsPermission: existStatsPermissions && !!permissions.bhstats.find(perm => perm.includes(MAIN_NODES.BEEHIVES_STATS + "_" + PERMISSIONS_ACTIONS.DELETE)),
            // users
            existViewUsersPermission: existUsersPermissions && !!permissions.users.find(perm => perm.includes(MAIN_NODES.USERS + "_" + PERMISSIONS_ACTIONS.VIEW)),
            existCreateUsersPermission: existUsersPermissions && !!permissions.users.find(perm => perm.includes(MAIN_NODES.USERS + "_" + PERMISSIONS_ACTIONS.CREATE)),
            existEditUsersPermission: existUsersPermissions && !!permissions.users.find(perm => perm.includes(MAIN_NODES.USERS + "_" + PERMISSIONS_ACTIONS.EDIT)),
            existDeleteUsersPermission: existUsersPermissions && !!permissions.users.find(perm => perm.includes(MAIN_NODES.USERS + "_" + PERMISSIONS_ACTIONS.DELETE)),
            // areas
            existViewAreasPermission: existAreasPermissions && !!permissions.areas.find(perm => perm.includes(MAIN_NODES.AREAS_EXPERTISE + "_" + PERMISSIONS_ACTIONS.VIEW)),
            existCreateAreasPermission: existAreasPermissions && !!permissions.areas.find(perm => perm.includes(MAIN_NODES.AREAS_EXPERTISE + "_" + PERMISSIONS_ACTIONS.CREATE)),
            existEditAreasPermission: existAreasPermissions && !!permissions.areas.find(perm => perm.includes(MAIN_NODES.AREAS_EXPERTISE + "_" + PERMISSIONS_ACTIONS.EDIT)),
            existDeleteAreasPermission: existAreasPermissions && !!permissions.areas.find(perm => perm.includes(MAIN_NODES.AREAS_EXPERTISE + "_" + PERMISSIONS_ACTIONS.DELETE)),
            // roles
            existViewRolesPermission: existRolesPermissions && !!permissions.roles.find(perm => perm.includes(MAIN_NODES.ROLES + "_" + PERMISSIONS_ACTIONS.VIEW)),
            existCreateRolesPermission: existRolesPermissions && !!permissions.roles.find(perm => perm.includes(MAIN_NODES.ROLES + "_" + PERMISSIONS_ACTIONS.CREATE)),
            existEditRolesPermission: existRolesPermissions && !!permissions.roles.find(perm => perm.includes(MAIN_NODES.ROLES + "_" + PERMISSIONS_ACTIONS.EDIT)),
            existDeleteRolesPermission: existRolesPermissions && !!permissions.roles.find(perm => perm.includes(MAIN_NODES.ROLES + "_" + PERMISSIONS_ACTIONS.DELETE)),
        }
    }

    const [permissionsState, setPermissionsState] = useState<IPermissionsState | undefined>(
        permissionsData ? getPermissionsState(permissionsData) : undefined
    )

    const setItem = (key: string, value: string) => {
        localStorage.setItem(key, value);
    };

    const removeItem = (key: string) => localStorage.removeItem(key);

    const clear = () => localStorage.clear()

    const login = (data: ILoginResponse | null) => {
        setLoginData(data);
        setItem(AUTH_KEY, JSON.stringify(data));
    };

    const setPermissions = (data: { [x: string]: string[] } | null) => {
        setPermissionsData(data);
        setPermissionsState(getPermissionsState(data));
        setItem(PERMISSIONS_KEY, JSON.stringify(data));
        navigate("/");
    };

    const logout = () => {
        setLoginData(null);
        removeItem(AUTH_KEY);
        clear();
        navigate("/login");
    };

    return (
        <AuthContext.Provider value={{ loginData, permissionsData, permissionsState, setLoginData, setPermissionsData, login, logout, setPermissions }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
